/**
 * RESPONSIBILITY: draw the header band.
 *
 * Content in, markup out. No fetching, no permission checks, no save handlers.
 *
 * One flat list of links becomes the bar and its panels, split on `parent`, so moving an
 * item between them is a word an editor types.
 *
 * TWO SHAPES, ONE MARKUP. On a wide screen the panels open on hover or focus, tracked in
 * state rather than CSS :hover: a click changes the hash under an SPA, so the mouse never
 * moves and the clicked link often keeps focus — a pure-CSS panel has no way to hear
 * "navigation happened" and stays open until the pointer physically leaves. State does,
 * because the click handler can close it directly. On a narrow screen the whole nav
 * collapses behind a toggle and the panels become sub-lists that are simply always shown,
 * because a dropdown inside a dropdown on a phone is a trap.
 *
 * OVER THE HERO on the home page: transparent, light text, laid on the photograph, and it
 * scrolls away with the photograph like the bar on every other page. It used to stay
 * pinned and turn solid on scroll; a bar that follows the reader on one page only reads as
 * a different site. Solid only while the phone menu is open, whose panel needs a ground.
 */
import { useState } from 'react';
import { hasImage } from '@framework/core';
import { BrandMark } from './BrandMark';
import type { HeaderSection, NavLink } from '@app/sections';
import { AccountMenu } from './AccountMenu';

const topLevel = (nav: NavLink[]) => nav.filter((n) => !n.parent?.trim());
const childrenOf = (nav: NavLink[], label: string) =>
  nav.filter((n) => n.parent?.trim().toLowerCase() === label.toLowerCase());

export function Header({ c, overHero = false }: { c: HeaderSection; overHero?: boolean }) {
  const nav = Array.isArray(c.nav) ? c.nav : [];
  const [open, setOpen] = useState(false);
  // Which top-level item's panel is open. One at a time, and closed by default.
  const [openLabel, setOpenLabel] = useState<string | null>(null);

  // Every link changes the hash, so the menu has to shut behind it — otherwise the new
  // page opens underneath a menu still covering it.
  const close = () => { setOpen(false); setOpenLabel(null); };

  return (
    <header className={`site-header${overHero ? ' is-over-hero' : ''}${overHero && !open ? ' is-clear' : ''}`}>
      <div className="header-bar">
        <a className="brand" href="#home" onClick={close}>
          <BrandMark logo={hasImage(c.logo) ? c.logo : undefined} name={c.fullName} />
          <b>{c.mark}</b>
          <span className="sr-only">{c.fullName}</span>
        </a>

        <button
          className="nav-toggle" type="button"
          aria-expanded={open} aria-controls="site-nav"
          onClick={() => setOpen(!open)}
        >
          <span aria-hidden="true">{open ? '✕' : '☰'}</span>
          <span className="sr-only">{open ? 'Close menu' : 'Open menu'}</span>
        </button>

        <nav id="site-nav" className={open ? 'main-nav is-open' : 'main-nav'}>
          {topLevel(nav).map((item) => {
            const kids = childrenOf(nav, item.label);
            const isOpen = openLabel === item.label;
            return (
              <div
                className="nav-item" key={item.label}
                onMouseEnter={() => setOpenLabel(item.label)}
                onMouseLeave={() => setOpenLabel((cur) => (cur === item.label ? null : cur))}
              >
                {item.href?.trim() ? (
                  <a
                    className={item.highlight ? 'nav-top nav-pill' : 'nav-top'}
                    href={item.href} onClick={close}
                    onFocus={() => setOpenLabel(item.label)}
                  >
                    {item.label}
                  </a>
                ) : (
                  /* No link and children below it: a heading that opens a panel, not a
                     link that pretends to go somewhere. */
                  <span
                    className="nav-top nav-heading" tabIndex={0}
                    onFocus={() => setOpenLabel(item.label)}
                  >
                    {item.label}
                  </span>
                )}

                {kids.length > 0 && (
                  <div className="nav-panel" style={{ display: isOpen ? 'block' : undefined }}>
                    {kids.map((k) => (
                      <a
                        className="nav-child" key={k.label} href={k.href || '#'}
                        onClick={close}
                      >
                        {k.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {/* Last, and part of the same bar — not a strip of its own above the site. */}
          <AccountMenu />
        </nav>
      </div>
    </header>
  );
}
